/**
 * Month values: everything in this app is a month and year, never a day.
 *
 * See specs/001-wine-cellar-tracker/data-model.md "Month values" and
 * spec.md FR-007/FR-013: purchases and tastings store `YYYY-MM` text, the
 * day is never accepted, stored or displayed, and "not later than the
 * current month" is judged in the Europe/Paris calendar.
 */

export interface MonthValue {
  year: number;
  month: number; // 1-12
}

const MONTH_VALUE_PATTERN = /^(\d{4})-(\d{2})$/;
export const APP_TIME_ZONE = 'Europe/Paris';

/** Parses a strict "YYYY-MM" string. Throws on anything else (including a day component). */
export function parseMonthValue(value: string): MonthValue {
  const match = MONTH_VALUE_PATTERN.exec(value);
  if (!match) {
    throw new Error(`Not a month value (expected "YYYY-MM"): ${JSON.stringify(value)}`);
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (month < 1 || month > 12) {
    throw new Error(`Month out of range 1-12: ${JSON.stringify(value)}`);
  }
  return { year, month };
}

/** Formats a MonthValue back to "YYYY-MM". */
/** A month as shown on a wine's page: `MM/YY`, e.g. "03/26" (owner feedback, 2026-09-28). */
export function formatMonthShort(value: MonthValue): string {
  return `${String(value.month).padStart(2, '0')}/${String(value.year % 100).padStart(2, '0')}`;
}

export function formatMonthValue(value: MonthValue): string {
  return `${value.year.toString().padStart(4, '0')}-${value.month.toString().padStart(2, '0')}`;
}

/** -1 if a < b, 0 if equal, 1 if a > b. */
export function compareMonths(a: MonthValue, b: MonthValue): number {
  if (a.year !== b.year) {
    return a.year < b.year ? -1 : 1;
  }
  if (a.month !== b.month) {
    return a.month < b.month ? -1 : 1;
  }
  return 0;
}

/** The MonthValue that is `count` whole months before `value`, crossing year boundaries. */
export function monthsBefore(value: MonthValue, count: number): MonthValue {
  const zeroBasedTotal = value.year * 12 + (value.month - 1) - count;
  const year = Math.floor(zeroBasedTotal / 12);
  const month = (zeroBasedTotal % 12) + 1;
  return { year, month };
}

/** The MonthValue that is `count` whole months after `value`. */
export function monthsAfter(value: MonthValue, count: number): MonthValue {
  return monthsBefore(value, -count);
}

/** Today's month in the Europe/Paris calendar (the app's single time zone). */
export function currentMonth(now: Date = new Date()): MonthValue {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(now);
  const year = Number(parts.find((p) => p.type === 'year')?.value);
  const month = Number(parts.find((p) => p.type === 'month')?.value);
  return { year, month };
}

/** Whether `value` is later than `today` (refused for purchases and tastings, per the edge cases). */
export function isFutureMonth(value: MonthValue, today: MonthValue = currentMonth()): boolean {
  return compareMonths(value, today) > 0;
}

/** A calendar day (a purchase's day since schema version 6, the drink-by due date). */
export interface CalendarDay {
  year: number;
  month: number;
  day: number;
}

/** Today in Paris (injectable `now` for tests). */
export function todayInParis(now: Date = new Date()): CalendarDay {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const part = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return { year: part('year'), month: part('month'), day: part('day') };
}

/** Whether the day exists (no 30 February). */
export function isRealDay({ year, month, day }: CalendarDay): boolean {
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    Number.isInteger(day) &&
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

/** Whether `day` is later than `today`. */
export function isFutureDay(day: CalendarDay, today: CalendarDay): boolean {
  return (day.year - today.year || day.month - today.month || day.day - today.day) > 0;
}
